import { AuthType } from "models/enums/auth";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import useAPIClientHelper from "./useAPIClientHelper";
import { store } from "store";
import { displayErrorMessage } from "store/actions/utilActions";
import { TranslationKeys } from "translations";

function useFileUploadService() {
  const { apiClientInstance } = useAPIClientHelper();

  const uploadFile = async (fileToUpload: UploadedFile) => {
    const result = await apiClientInstance(AuthType.Company).FileClient.UploadFileAsync(fileToUpload);
    return result.data.uploadedFileId;
  };

  const uploadFiles = async (filesToUpload: UploadedFile[]) => {
    const ids: string[] = [];
    for (const file of filesToUpload) {
      try {
        const id = await uploadFile(file);
        if (id == null) {
          throw Error("Failed to upload file");
        }
        ids.push(id);
      } catch (e) {
        store.dispatch(
          displayErrorMessage(
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FileUploadError, undefined, {
              fileName: file.fileName,
            })
          )
        );
        throw e;
      }
    }
    return ids;
  };

  const updateMissingAttachments = async (
    filesToUpload: UploadedFile[],
    getFile: (attachmentId: string | number) => Promise<string>
  ) => {
    const filesWithoutAttachments = filesToUpload?.filter((f) => f.id && UtilsHelper.isEmpty(f.data));

    const missingFileContents = await Promise.all(
      filesWithoutAttachments?.map((f) => {
        return getFile(f.id!).then((res) => ({
          id: f.id,
          data: res,
        }));
      }) ?? []
    );

    return filesToUpload?.map((file) =>
      UtilsHelper.isEmpty(file.data)
        ? { ...file, data: missingFileContents.find((m) => m.id === file.id)?.data ?? "" }
        : file
    );
  };

  return {
    uploadFile,
    uploadFiles,
    updateMissingAttachments,
  };
}

export default useFileUploadService;
